import React, { useState, useRef, useEffect } from 'react';
import { flushSync } from 'react-dom';
import { RefreshCw, AlertCircle, Search, ChevronDown, Check, X, Trash2, RotateCcw, Copy } from 'lucide-react';
import axios from 'axios';
import useSWR from 'swr';
import API_BASE from '../api';
import { useNavigate } from 'react-router-dom';
import {
  formatDashboardTimestamp,
  formatFirstClassFromPicker,
  formatSlotLineForDashboard,
  parseFirstClassSortKey,
  slotMatchesPrimaryEastern,
  displayPrimaryTimeSlot,
} from '../utils/easternTime';

const BRAND = '#c1009d';
const NOT_FOUND_COACH = '__NOT_FOUND__';
const UNASSIGNED_COACH = '__UNASSIGNED__';
const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const HOURS = Array.from({ length: 16 }, (_, i) => i + 6); // 6am–9pm

const dashboardFetcher = async (url) => {
  const res = await axios.get(url);
  return {
    requests: res.data.requests || [],
    coaches: res.data.coaches || [],
    courses: res.data.courses || []
  };
};

function ConfirmModal({ open, title, message, confirmLabel, cancelLabel = 'Cancel', danger, onConfirm, onCancel }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  if (!open) return null;

  const showCancel = cancelLabel !== null && cancelLabel !== undefined && String(cancelLabel).trim() !== '';

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1.25rem',
      }}
      role="presentation"
      onClick={onCancel}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(15, 23, 42, 0.45)',
        }}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-modal-title"
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: 420,
          background: '#fff',
          borderRadius: 14,
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
          border: '1px solid #e5e7eb',
          overflow: 'hidden',
        }}
      >
        <div style={{ padding: '1.35rem 1.35rem 0.75rem' }}>
          <h2 id="confirm-modal-title" style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: '#111827' }}>
            {title}
          </h2>
          {message ? (
            <p style={{ margin: '0.75rem 0 0', fontSize: '0.92rem', lineHeight: 1.55, color: '#4b5563' }}>{message}</p>
          ) : null}
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '0.65rem',
            padding: '1rem 1.35rem 1.25rem',
            background: '#f9fafb',
            borderTop: '1px solid #f3f4f6',
          }}
        >
          {showCancel && (
            <button
              type="button"
              onClick={onCancel}
              style={{
                padding: '0.5rem 1rem',
                fontSize: '0.88rem',
                fontWeight: 600,
                borderRadius: 8,
                border: '1px solid #d1d5db',
                background: '#fff',
                color: '#374151',
                cursor: 'pointer',
              }}
            >
              {cancelLabel}
            </button>
          )}
          <button
            type="button"
            onClick={onConfirm}
            style={{
              padding: '0.5rem 1rem',
              fontSize: '0.88rem',
              fontWeight: 600,
              borderRadius: 8,
              border: 'none',
              background: danger ? '#dc2626' : BRAND,
              color: '#fff',
              cursor: 'pointer',
              boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function Dashboard() {
  const [listView, setListView] = useState('active'); // 'active' | 'trash'
  const { data = {}, error, isLoading, mutate } = useSWR(
    `${API_BASE}/api/dashboard?view=${listView}`,
    dashboardFetcher,
    {
      revalidateOnFocus: true,
      dedupingInterval: 10000,
      refreshInterval: 15000,
      refreshWhenHidden: false,
      refreshWhenOffline: false,
    }
  );
  const requests = data.requests || [];
  const coaches = data.coaches || [];
  const [searchQuery, setSearchQuery] = useState('');
  const [classFilter, setClassFilter] = useState('All');
  const [coachFilter, setCoachFilter] = useState('All');
  const [termStartFilter, setTermStartFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [sortKey, setSortKey] = useState('last_updated');
  const [sortDir, setSortDir] = useState('desc'); // default: latest first
  const [expandedId, setExpandedId] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null); // { type: 'trash'|'restore', id, name }
  const [actionError, setActionError] = useState(null);
  const [copyState, setCopyState] = useState({ copied: false, toast: false });
  // share-link email removed

  const loading = isLoading;

  const nowIso = () => new Date().toISOString();

  // If the backend was updated and `courses` isn't present yet (older cached response),
  // force a re-fetch so the Class dropdown has the full list.
  React.useEffect(() => {
    if (!data || !('courses' in data) || !Array.isArray(data.courses)) {
      mutate();
    }
  }, [data, mutate]);

  useEffect(() => {
    if (!actionError) return;
    const t = setTimeout(() => setActionError(null), 8000);
    return () => clearTimeout(t);
  }, [actionError]);

  const updateRequestOptimistic = async (requestId, patch, apiCall) => {
    const prev = data;
    // Optimistic update: patch row immediately + bump last_updated so it sorts to top
    mutate((curr) => {
      const requests0 = curr?.requests || [];
      const nextRequests = requests0.map((r) => {
        if (r.id !== requestId) return r;
        return { ...r, ...patch, last_updated: nowIso() };
      });
      return { ...(curr || {}), requests: nextRequests };
    }, false);

    try {
      await apiCall();
      // Revalidate to ensure backend truth
      await mutate();
    } catch (e) {
      // Rollback
      mutate(prev, false);
      throw e;
    }
  };

  // (matching actions live elsewhere; keeping this dashboard focused on review + edits)

  const openTrashConfirm = (r) => {
    setActionError(null);
    setConfirmDialog({
      type: 'trash',
      id: r.id,
      name: String(r.student_name || '').trim() || 'this student',
    });
  };

  const openRestoreConfirm = (r) => {
    setActionError(null);
    setConfirmDialog({
      type: 'restore',
      id: r.id,
      name: String(r.student_name || '').trim() || 'this student',
    });
  };

  const copyFormLink = async () => {
    const url = 'https://client4.appicai.com/';
    try {
      if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        await navigator.clipboard.writeText(url);
      } else {
        const ta = document.createElement('textarea');
        ta.value = url;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopyState({ copied: true, toast: true });
      window.setTimeout(() => setCopyState((s) => ({ ...s, toast: false })), 1800);
      window.setTimeout(() => setCopyState((s) => ({ ...s, copied: false })), 1200);
    } catch (e) {
      setCopyState({ copied: false, toast: false });
    }
  };

  /** Removes row from the current list immediately; rolls back if the API fails. */
  const executeTrashOrRestore = async (d) => {
    if (!d) return;
    const prev = data;
    // Commit list + close modal in one paint so the row disappears as soon as the dialog closes (no lag).
    flushSync(() => {
      setExpandedId((eid) => (eid === d.id ? null : eid));
      mutate((curr) => {
        const reqs = (curr?.requests || []).filter((r) => r.id !== d.id);
        return { ...(curr || {}), requests: reqs, count: reqs.length };
      }, false);
      setConfirmDialog(null);
    });
    try {
      if (d.type === 'trash') {
        await axios.patch(`${API_BASE}/api/dashboard/${d.id}/soft-delete`);
      } else {
        await axios.patch(`${API_BASE}/api/dashboard/${d.id}/restore`);
      }
    } catch (err) {
      flushSync(() => {
        mutate(prev, false);
      });
      setActionError(err.response?.data?.error || err.message || 'Something went wrong');
    }
  };

  // Show all student records (no status filter)
  const allStudents = requests;

  const normalizeClassName = (s) => {
    // Airtable may store curly quotes; normalize to straight apostrophes.
    return String(s || '')
      .replace(/[’‘]/g, "'")
      .replace(/\s+/g, ' ')
      .trim();
  };

  // Class list should come from Airtable "Courses" table (via backend payload),
  // with row-derived fallback so existing values always remain selectable.
  const backendCourses = Array.isArray(data.courses) ? data.courses : [];
  const canonicalByNorm = new Map(
    backendCourses
      .map((c) => String(c || '').trim())
      .filter(Boolean)
      .map((c) => [normalizeClassName(c), c])
  );

  // "Class" values should be individual programs (not comma-joined combinations).
  // Some requests may have multiple programs, and `class_name` can become "A, B".
  // We flatten `programs` and also split `class_name` by comma as a fallback.
  const resolveClassesForRow = (r) => {
    const out = [];
    if (Array.isArray(r.programs) && r.programs.length > 0) {
      out.push(...r.programs.map((p) => String(p).trim()).filter(Boolean));
    } else if (r.program) {
      out.push(String(r.program).trim());
    }

    // Include current row class_name (overrides may set a single value).
    if (typeof r.class_name === 'string' && r.class_name.trim()) {
      out.push(r.class_name.trim());
    }

    // Fallback: if class_name is already a comma-separated list
    if (typeof r.class_name === 'string' && r.class_name.trim() && r.class_name.includes(',')) {
      out.push(...r.class_name.split(',').map((s) => s.trim()).filter(Boolean));
    }

    // Keep every row class value (mapped through Airtable canonical names when available).
    const canonical = out.map((x) => {
      const n = normalizeClassName(x);
      return canonicalByNorm.get(n) || x;
    });
    return canonical.filter(Boolean);
  };

  // Dropdown options: Airtable Courses + any row values not currently in Courses.
  const classSet = new Set(
    backendCourses
      .map((c) => String(c || '').trim())
      .filter(Boolean)
  );
  for (const r of allStudents) {
    for (const c of resolveClassesForRow(r)) {
      if (c) classSet.add(String(c).trim());
    }
  }
  const classOptions = Array.from(classSet).sort((a, b) => String(a).localeCompare(String(b)));
  const classOptionsSet = new Set(classOptions);
  const resolveClassValueForRow = (r) => {
    const rowClasses = resolveClassesForRow(r);
    // Prefer a value that is present in the dropdown options.
    const preferred = rowClasses.find((c) => classOptionsSet.has(c));
    return preferred || '';
  };

  const coachOptions = Array.from(
    new Set(allStudents.map((r) => r.selected_coach).filter((v) => v !== null && v !== undefined && v !== ''))
  ).sort((a, b) => String(a).localeCompare(String(b)));

  const termStartOptions = Array.from(
    new Set(allStudents.map((r) => r.term_start_date).filter(Boolean))
  ).sort((a, b) => String(a).localeCompare(String(b)));

  const statusOptions = Array.from(
    new Set(allStudents.map((r) => r.matching_status).filter(Boolean))
  ).sort((a, b) => String(a).localeCompare(String(b)));

  const resolveCoachForFilter = (r) => {
    if (!r || r.selected_coach === null || r.selected_coach === undefined || r.selected_coach === '') return UNASSIGNED_COACH;
    return r.selected_coach;
  };

  const searchLower = searchQuery.trim().toLowerCase();
  const filtered = allStudents
    .filter((r) => {
      if (classFilter === 'All') return true;
      const rowClasses = resolveClassesForRow(r);
      return rowClasses.some((c) => String(c) === String(classFilter));
    })
    .filter((r) => (coachFilter === 'All' ? true : String(resolveCoachForFilter(r)) === String(coachFilter)))
    .filter((r) => (termStartFilter === 'All' ? true : String(r.term_start_date || '') === String(termStartFilter)))
    .filter((r) => (statusFilter === 'All' ? true : String(r.matching_status || '') === String(statusFilter)))
    .filter((r) => (searchLower ? (r.student_name || '').toLowerCase().includes(searchLower) : true));

  const sorted = [...filtered].sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    if (sortKey === 'last_updated') {
      const ad = a.last_updated ? new Date(a.last_updated).getTime() : 0;
      const bd = b.last_updated ? new Date(b.last_updated).getTime() : 0;
      return (ad - bd) * dir;
    }
    if (sortKey === 'primary_time_slot') {
      const na = parseFirstClassSortKey(a.primary_time_slot, a.timezone);
      const nb = parseFirstClassSortKey(b.primary_time_slot, b.timezone);
      const aEmpty = na == null;
      const bEmpty = nb == null;
      if (aEmpty && bEmpty) return 0;
      if (aEmpty) return 1;
      if (bEmpty) return -1;
      return (na - nb) * dir;
    }
    const av = a[sortKey] ?? '';
    const bv = b[sortKey] ?? '';
    return String(av).localeCompare(String(bv)) * dir;
  });

  const handleSort = (nextKey) => {
    if (sortKey === nextKey) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(nextKey);
      // First class: earliest → latest by default; Last updated: newest first by default
      setSortDir(nextKey === 'last_updated' ? 'desc' : 'asc');
    }
  };

  // Status filter counts (commented out - can uncomment for status tabs: All, Pending, Matched, etc.)
  // const statusCounts = {};
  // for (const r of requests) {
  //   const s = r.matching_status || 'Pending';
  //   statusCounts[s] = (statusCounts[s] || 0) + 1;
  // }

  if (loading) {
    return (
      <div style={{ padding: '2rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <div className="loading-spinner"></div>
          <span style={{ color: '#666' }}>Loading student data...</span>
        </div>
      </div>
    );
  }

  if (error) {
    const errMsg = error?.response?.data?.error || error?.message || 'Failed to load';
    return (
      <div style={{ padding: '2rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#ef4444', marginBottom: '1rem' }}>
          <AlertCircle size={20} />
          <span>Error: {errMsg}</span>
        </div>
        <button onClick={() => mutate()} style={btnSecondary}>
          <RefreshCw size={14} /> Retry
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: '1.5rem 2rem' }}>
      {copyState.toast && (
        <div
          style={{
            position: 'fixed',
            top: 18,
            right: 18,
            zIndex: 2000,
            background: '#111827',
            color: '#fff',
            borderRadius: 12,
            padding: '0.6rem 0.85rem',
            fontSize: '0.85rem',
            fontWeight: 700,
            boxShadow: '0 12px 30px rgba(0,0,0,0.22)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
          }}
          role="status"
          aria-live="polite"
        >
          <Check size={16} />
          Copied
        </div>
      )}
      {actionError && (
        <div
          style={{
            marginBottom: '1rem',
            padding: '0.75rem 1rem',
            borderRadius: 10,
            background: '#fef2f2',
            border: '1px solid #fecaca',
            color: '#991b1b',
            fontSize: '0.88rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '0.75rem',
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <AlertCircle size={18} />
            {actionError}
          </span>
          <button
            type="button"
            onClick={() => setActionError(null)}
            style={{
              border: 'none',
              background: 'transparent',
              color: '#991b1b',
              cursor: 'pointer',
              padding: '0.25rem',
              lineHeight: 1,
            }}
            aria-label="Dismiss"
          >
            <X size={18} />
          </button>
        </div>
      )}

      <ConfirmModal
        open={!!confirmDialog}
        title={
          confirmDialog?.type === 'trash'
            ? `Move “${confirmDialog.name}” to trash?`
            : `Restore “${confirmDialog?.name || ''}”?`
        }
        message=""
        confirmLabel={confirmDialog?.type === 'trash' ? 'Move to trash' : 'Restore'}
        danger={confirmDialog?.type === 'trash'}
        onCancel={() => setConfirmDialog(null)}
        onConfirm={() => executeTrashOrRestore(confirmDialog)}
      />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0, color: '#1a1a2e' }}>Student Dashboard</h1>
          <p style={{ margin: '0.25rem 0 0', color: '#888', fontSize: '0.9rem' }}>
            {listView === 'trash'
              ? `${allStudents.length} record${allStudents.length === 1 ? '' : 's'} in trash`
              : `${allStudents.length} student records`}
            <span style={{ display: 'block', marginTop: '0.2rem', fontSize: '0.82rem', color: '#9ca3af' }}>
              Times are shown in US Eastern (EST).
            </span>
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          {/* Status filter tabs - commented out. Uncomment to filter by: All, Pending, Matched, etc. */}
          {/* <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button style={btnSecondary}>All</button>
            <button style={btnSecondary}>Pending</button>
            <button style={btnSecondary}>Match Found</button>
            <button style={btnSecondary}>Matched</button>
            <button style={btnSecondary}>Blocked</button>
            <button style={btnSecondary}>In Progress</button>
          </div> */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: '0.5rem',
            padding: '0.5rem 0.85rem', border: '1px solid #e5e7eb', borderRadius: 8,
            background: '#fff', minWidth: 220,
          }}>
            <Search size={16} color="#9ca3af" />
            <input
              type="text"
              placeholder="Search by student name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ border: 'none', outline: 'none', fontSize: '0.88rem', flex: 1, background: 'transparent' }}
            />
          </div>
        
        </div>
      </div>

      <div style={{ marginBottom: '1rem', display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center' }}>
        <div style={{ display: 'flex', borderRadius: 8, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
          <button
            type="button"
            onClick={() => setListView('active')}
            style={{
              padding: '0.45rem 0.9rem',
              fontSize: '0.85rem',
              fontWeight: 600,
              border: 'none',
              cursor: 'pointer',
              background: listView === 'active' ? BRAND : '#fff',
              color: listView === 'active' ? '#fff' : '#374151',
            }}
          >
            Active
          </button>
          <button
            type="button"
            onClick={() => setListView('trash')}
            style={{
              padding: '0.45rem 0.9rem',
              fontSize: '0.85rem',
              fontWeight: 600,
              border: 'none',
              borderLeft: '1px solid #e5e7eb',
              cursor: 'pointer',
              background: listView === 'trash' ? '#64748b' : '#fff',
              color: listView === 'trash' ? '#fff' : '#374151',
            }}
          >
            Trash
          </button>
        </div>

        <select
          value={classFilter}
          onChange={(e) => setClassFilter(e.target.value)}
          style={{
            padding: '0.5rem 0.75rem',
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            background: '#fff',
            fontSize: '0.85rem',
            color: '#374151',
          }}
        >
          <option value="All">Class: All</option>
          {classOptions.map((v) => (
            <option key={v} value={v}>{v}</option>
          ))}
        </select>

        <select
          value={coachFilter}
          onChange={(e) => setCoachFilter(e.target.value)}
          style={{
            padding: '0.5rem 0.75rem',
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            background: '#fff',
            fontSize: '0.85rem',
            color: '#374151',
          }}
        >
          <option value="All">Coach: All</option>
          <option value={UNASSIGNED_COACH}>Coach: Unassigned</option>
          <option value={NOT_FOUND_COACH}>Coach: Not Found</option>
          {coachOptions
            .filter((v) => v !== NOT_FOUND_COACH)
            .map((v) => (
              <option key={v} value={v}>{v}</option>
            ))}
        </select>

        <select
          value={termStartFilter}
          onChange={(e) => setTermStartFilter(e.target.value)}
          style={{
            padding: '0.5rem 0.75rem',
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            background: '#fff',
            fontSize: '0.85rem',
            color: '#374151',
          }}
        >
          <option value="All">Term Start: All</option>
          {termStartOptions.map((v) => (
            <option key={v} value={v}>{v}</option>
          ))}
        </select>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{
            padding: '0.5rem 0.75rem',
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            background: '#fff',
            fontSize: '0.85rem',
            color: '#374151',
          }}
        >
          <option value="All">Matching Status: All</option>
          {statusOptions.map((v) => (
            <option key={v} value={v}>{v}</option>
          ))}
        </select>

        <button
          type="button"
          onClick={copyFormLink}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '0.5rem 0.85rem',
            borderRadius: 10,
            border: '1px solid #e5e7eb',
            background: '#fff',
            color: '#111827',
            fontSize: '0.85rem',
            fontWeight: 700,
            cursor: 'pointer',
            boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
            marginLeft: 'auto',
          }}
          title="Copy student form link"
        >
          {copyState.copied ? <Check size={16} /> : <Copy size={16} />}
          Copy form link
        </button>
      </div>

      <div style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
        <div style={{ overflowX: 'auto', overflowY: 'visible', WebkitOverflowScrolling: 'touch', scrollbarGutter: 'stable' }} className="dashboard-table-scroll">
        <table style={{ width: '100%', minWidth: 1160, borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #eee' }}>
              <Th onClick={() => handleSort('student_name')} active={sortKey === 'student_name'} dir={sortKey === 'student_name' ? sortDir : 'asc'}>Student</Th>
              <Th onClick={() => handleSort('parent_name')} style={{ textAlign: 'center' }} active={sortKey === 'parent_name'} dir={sortKey === 'parent_name' ? sortDir : 'asc'}>Parent Full Name</Th>
              <Th onClick={() => handleSort('parent_email')} style={{ textAlign: 'center' }} active={sortKey === 'parent_email'} dir={sortKey === 'parent_email' ? sortDir : 'asc'}>Parent Email</Th>
              <Th onClick={() => handleSort('class_name')} style={{ textAlign: 'center' }} active={sortKey === 'class_name'} dir={sortKey === 'class_name' ? sortDir : 'asc'}>Class</Th>
              <Th onClick={() => handleSort('selected_coach')} style={{ textAlign: 'center', minWidth: 130 }} active={sortKey === 'selected_coach'} dir={sortKey === 'selected_coach' ? sortDir : 'asc'}>Coach Name</Th>
              <Th onClick={() => handleSort('primary_time_slot')} style={{ textAlign: 'center', minWidth: 170 }} active={sortKey === 'primary_time_slot'} dir={sortKey === 'primary_time_slot' ? sortDir : 'asc'}>First class</Th>
              <Th onClick={() => handleSort('term_start_date')} style={{ textAlign: 'center' }} active={sortKey === 'term_start_date'} dir={sortKey === 'term_start_date' ? sortDir : 'asc'}>Term Start</Th>
              <Th onClick={() => handleSort('matching_status')} style={{ textAlign: 'center', minWidth: 148 }} active={sortKey === 'matching_status'} dir={sortKey === 'matching_status' ? sortDir : 'asc'}>Matching Status</Th>
              <Th style={{ textAlign: 'center', minWidth: 88 }}>Booked</Th>
              <Th onClick={() => handleSort('coach_options')} style={{ textAlign: 'center' }} active={sortKey === 'coach_options'} dir={sortKey === 'coach_options' ? sortDir : 'asc'}>Coach options</Th>
              <Th onClick={() => handleSort('last_updated')} style={{ textAlign: 'center' }} active={sortKey === 'last_updated'} dir={sortDir}>Last Updated</Th>
              <Th style={{ textAlign: 'center', width: 56 }}> </Th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((r) => {
              const isExpanded = expandedId === r.id;
              return (
                <React.Fragment key={r.id}>
                  <tr
                    style={{
                      borderBottom: '1px solid #f0f0f0', cursor: 'pointer',
                      background: isExpanded ? '#faf8fc' : '#fff', transition: 'background 0.15s',
                    }}
                    onClick={() => setExpandedId(isExpanded ? null : r.id)}
                    onMouseEnter={e => { if (!isExpanded) e.currentTarget.style.background = '#fafafa'; }}
                    onMouseLeave={e => { if (!isExpanded) e.currentTarget.style.background = '#fff'; }}
                  >
                    <Td><span style={{ fontWeight: 600, color: '#222' }}>{r.student_name || '—'}</span></Td>
                    <Td style={{ textAlign: 'center' }}><span style={{ fontSize: '0.82rem', color: '#444' }}>{r.parent_name || '—'}</span></Td>
                    <Td style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                      <InlineEditableText
                        value={r.parent_email || ''}
                        placeholder="—"
                        onSave={async (next) => {
                          await updateRequestOptimistic(
                            r.id,
                            { parent_email: next },
                            () => axios.patch(`${API_BASE}/api/dashboard/${r.id}/overrides`, { parent_email: next })
                          );
                        }}
                      />
                    </Td>
                    <Td style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                      <InlineSelect
                        value={resolveClassValueForRow(r)}
                        placeholder="—"
                        options={classOptions}
                        onSave={async (next) => {
                          await updateRequestOptimistic(
                            r.id,
                            { class_name: next },
                            () => axios.patch(`${API_BASE}/api/dashboard/${r.id}/overrides`, { class_name: next })
                          );
                        }}
                      />
                    </Td>
                    <Td style={{ textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                      <CoachDropdown
                        requestId={r.id}
                        selectedCoach={r.selected_coach}
                        coaches={coaches}
                        onOptimisticUpdated={(patch) => {
                          mutate((curr) => {
                            const requests0 = curr?.requests || [];
                            // Coach changes can trigger a background revalidate. If we optimistically bump `last_updated`,
                            // the row jumps to the top, then can jump back down when the re-fetch returns, and then up again.
                            // Keep the row stable during loading by not touching sort keys here.
                            const nextRequests = requests0.map((x) => x.id === r.id ? { ...x, ...patch } : x);
                            return { ...(curr || {}), requests: nextRequests };
                          }, false);
                        }}
                        onUpdated={() => mutate()}
                      />
                    </Td>
                    <Td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                      <PrimaryTimeSlotDropdown
                        studentTimezone={r.timezone}
                        value={r.primary_time_slot || ''}
                        onSave={async (next) => {
                          await updateRequestOptimistic(
                            r.id,
                            { primary_time_slot: next },
                            () => axios.patch(`${API_BASE}/api/dashboard/${r.id}/overrides`, { primary_time_slot: next })
                          );
                        }}
                      />
                    </Td>
                    <Td style={{ textAlign: 'center' }}><span style={{ fontSize: '0.82rem', color: '#444' }}>{r.term_start_date || '—'}</span></Td>
                    <Td style={{ textAlign: 'center', verticalAlign: 'middle' }} onClick={(e) => e.stopPropagation()}>
                      {r.matching_status === 'Needs Attention' && listView !== 'trash' ? (
                        <NeedsAttentionResolvePanel request={r} onUpdated={() => mutate()} />
                      ) : (r.matching_status === 'Awaiting Selection'
                          && listView !== 'trash'
                          && r.selected_coach
                          && r.selected_coach !== NOT_FOUND_COACH
                          && r.selected_coach !== UNASSIGNED_COACH) ? (
                        <AwaitingResolvePanel request={r} onUpdated={() => mutate()} />
                      ) : (
                        <MatchStatusBadge status={r.matching_status} />
                      )}
                    </Td>
                    <Td style={{ textAlign: 'center', verticalAlign: 'middle' }} onClick={(e) => e.stopPropagation()}>
                      {listView === 'trash' ? '—' : <BookedCheckboxCell request={r} onUpdated={() => mutate()} />}
                    </Td>
                    <Td style={{ textAlign: 'center' }}><span style={{ fontWeight: 600, color: '#333', fontSize: '0.95rem' }}>{r.coach_options || 0}</span></Td>
                    <Td style={{ textAlign: 'center' }}>
                      <span style={{ fontSize: '0.82rem', color: '#444' }}>
                        {r.last_updated ? formatDashboardTimestamp(r.last_updated) : '—'}
                      </span>
                    </Td>
                    <Td
                      style={{ textAlign: 'center', verticalAlign: 'middle', width: 56, padding: '0.35rem' }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {listView === 'trash' ? (
                        <button
                          type="button"
                          title="Restore student"
                          onClick={() => openRestoreConfirm(r)}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 34,
                            height: 34,
                            borderRadius: 8,
                            border: '1px solid #bbf7d0',
                            background: '#f0fdf4',
                            color: '#15803d',
                            cursor: 'pointer',
                          }}
                        >
                          <RotateCcw size={16} strokeWidth={2.25} />
                        </button>
                      ) : (
                        <button
                          type="button"
                          title="Move to trash"
                          onClick={() => openTrashConfirm(r)}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 34,
                            height: 34,
                            borderRadius: 8,
                            border: '1px solid #fecaca',
                            background: '#fef2f2',
                            color: '#b91c1c',
                            cursor: 'pointer',
                          }}
                        >
                          <Trash2 size={16} strokeWidth={2.25} />
                        </button>
                      )}
                    </Td>
                  </tr>
                  {isExpanded && (
                    <tr>
                      <td colSpan="12" style={{ padding: '1rem 1.5rem', background: '#faf8fc', borderBottom: '1px solid #eee' }}>
                        <ExpandedRow request={r} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {sorted.length === 0 && (
              <tr>
                <td colSpan="12" style={{ textAlign: 'center', padding: '3rem', color: '#999' }}>
                  {searchQuery.trim()
                    ? `No student records match "${searchQuery}"`
                    : listView === 'trash'
                      ? 'Trash is empty'
                      : 'No student records'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}

function CoachDropdown({ requestId, selectedCoach, coaches, onUpdated, onOptimisticUpdated }) {
  const [open, setOpen] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [blockedModal, setBlockedModal] = useState(null); // { title, message }
  const ref = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = async (coachName) => {
    if (coachName === selectedCoach) { setOpen(false); return; }
    setUpdating(true);
    try {
      const nextCoach = coachName === NOT_FOUND_COACH ? NOT_FOUND_COACH : coachName;
      onOptimisticUpdated?.({ selected_coach: nextCoach });
      await axios.patch(`${API_BASE}/api/dashboard/${requestId}/coach`, { coach_name: coachName });
      setOpen(false);
      onUpdated?.();
    } catch (err) {
      alert('Failed to update coach: ' + (err.response?.data?.error || err.message));
    } finally {
      setUpdating(false);
    }
  };

  const effectiveCoach = selectedCoach === NOT_FOUND_COACH ? NOT_FOUND_COACH : selectedCoach;
  const displayName = effectiveCoach === NOT_FOUND_COACH ? 'Not Found' : (effectiveCoach || 'Select coach');
  const coachList = coaches.length > 0 ? coaches : [];
  const buttonBg = effectiveCoach === NOT_FOUND_COACH ? '#fee2e2' : effectiveCoach ? '#dcfce7' : '#f3f4f6';
  const buttonColor = effectiveCoach === NOT_FOUND_COACH ? '#991b1b' : effectiveCoach ? '#166534' : '#6b7280';

  return (
    <>
      {blockedModal && (
        <ConfirmModal
          open
          title={blockedModal.title}
          message={blockedModal.message}
          confirmLabel="OK"
          cancelLabel=""
          onConfirm={() => setBlockedModal(null)}
          onCancel={() => setBlockedModal(null)}
        />
      )}
    <div ref={ref} style={{ position: 'relative', display: 'inline-block', minWidth: 0 }}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        disabled={updating}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 5,
          padding: '0.35rem 0.7rem',
          background: buttonBg,
          color: buttonColor,
          border: 'none',
          borderRadius: 999,
          fontSize: '0.8rem',
          fontWeight: 600,
          cursor: updating ? 'wait' : 'pointer',
          whiteSpace: 'nowrap',
          maxWidth: 160,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {updating ? (
          <span className="loading-spinner" style={{ width: 14, height: 14, borderWidth: 2, marginRight: 2 }} />
        ) : (
          <>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{displayName}</span>
            <ChevronDown size={12} style={{ opacity: open ? 1 : 0.7, flexShrink: 0 }} />
          </>
        )}
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            marginTop: 4,
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            zIndex: 100,
            minWidth: 180,
            maxWidth: 220,
            maxHeight: 240,
            overflowY: 'auto',
          }}
        >
          <div
            onClick={(e) => { e.stopPropagation(); handleSelect(NOT_FOUND_COACH); }}
            style={{
              padding: '0.5rem 0.75rem',
              cursor: 'pointer',
              background: '#fef2f2',
              color: '#991b1b',
              fontWeight: effectiveCoach === NOT_FOUND_COACH ? 700 : 600,
              fontSize: '0.85rem',
              borderRadius: 8,
              marginBottom: 6,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.filter = 'brightness(0.98)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.filter = 'none'; }}
          >
            Not Found
          </div>

          {coachList.map((c) => {
            const name = c.coach_name || c.name || '';
            const isSelected = name === effectiveCoach;
            const paused = c.accepting_students === false;
            const full = c.remaining_slots === 0 || c.max_students === 0;
            const warn = paused || full;
            const suffix = paused && full
              ? ' (PAUSED BY ADMIN · MAX LIMIT REACHED)'
              : paused
                ? ' (PAUSED BY ADMIN)'
                : full
                  ? ' (MAX LIMIT REACHED)'
                  : '';
            return (
              <div
                key={c.id || name}
                onClick={(e) => {
                  e.stopPropagation();
                  if (warn) {
                    const title = paused && full
                      ? 'Coach unavailable'
                      : paused
                        ? 'Coach paused'
                        : 'Maximum capacity';
                    const message = paused && full
                      ? 'This coach is paused by an administrator and has reached the maximum number of students. You cannot select this coach.'
                      : paused
                        ? 'This coach is paused by an administrator and is not accepting new students. You cannot select this coach.'
                        : 'This coach has reached the maximum student limit. You cannot select this coach.';
                    setBlockedModal({ title, message });
                    return;
                  }
                  handleSelect(name);
                }}
                style={{
                  padding: '0.5rem 0.75rem',
                  cursor: warn ? 'not-allowed' : 'pointer',
                  background: isSelected
                    ? (warn ? '#fff1f2' : '#f0fdf4')
                    : (warn ? '#fff7f7' : 'transparent'),
                  fontWeight: isSelected ? 800 : 600,
                  color: warn ? '#991b1b' : (isSelected ? '#166534' : '#374151'),
                  fontSize: '0.85rem',
                  opacity: warn ? 0.95 : 1,
                }}
                onMouseEnter={(e) => {
                  if (warn) return;
                  if (!isSelected) e.currentTarget.style.background = '#f9fafb';
                }}
                onMouseLeave={(e) => {
                  if (warn) return;
                  if (!isSelected) e.currentTarget.style.background = 'transparent';
                  else e.currentTarget.style.background = '#f0fdf4';
                }}
              >
                {name}{suffix}
              </div>
            );
          })}
          {coachList.length === 0 && (
            <div style={{ padding: '0.75rem', color: '#9ca3af', fontSize: '0.85rem' }}>No coaches loaded</div>
          )}
        </div>
      )}
    </div>
    </>
  );
}

function InlineEditableText({ value, placeholder, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => { if (!editing) setDraft(value || ''); }, [value, editing]);

  const commit = async () => {
    const next = String(draft || '').trim();
    if (!next) return;
    setSaving(true);
    setErr(null);
    try {
      await onSave(next);
      setEditing(false);
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        style={{
          background: 'transparent',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          color: value ? '#444' : '#bbb',
          fontSize: '0.82rem',
          fontWeight: 500,
          textDecoration: 'underline',
          textUnderlineOffset: 3,
        }}
        title="Edit"
      >
        {value || placeholder}
      </button>
    );
  }

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, justifyContent: 'center' }}>
      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        style={{
          width: 220,
          padding: '0.35rem 0.5rem',
          border: '1px solid #e5e7eb',
          borderRadius: 8,
          fontSize: '0.82rem',
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') setEditing(false);
        }}
        disabled={saving}
        autoFocus
      />
      <button type="button" onClick={commit} disabled={saving} style={miniIconBtn} title="Save">
        {saving ? (
          <span className="loading-spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />
        ) : (
          <Check size={14} />
        )}
      </button>
      <button type="button" onClick={() => setEditing(false)} disabled={saving} style={miniIconBtn} title="Cancel">
        <X size={14} />
      </button>
      {err && <span style={{ display: 'block', color: '#b91c1c', fontSize: '0.75rem' }}>{err}</span>}
    </div>
  );
}

function InlineSelect({ value, placeholder, options, onSave }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const display = value || placeholder;
  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        disabled={saving}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '0.35rem 0.7rem',
          background: '#f3f4f6',
          color: '#111827',
          border: 'none',
          borderRadius: 999,
          fontSize: '0.8rem',
          fontWeight: 600,
          cursor: saving ? 'wait' : 'pointer',
          maxWidth: 220,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title="Edit class"
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{display}</span>
        {saving ? (
          <span className="loading-spinner" style={{ width: 14, height: 14, borderWidth: 2, marginLeft: 4 }} />
        ) : (
          <ChevronDown size={12} style={{ opacity: open ? 1 : 0.7 }} />
        )}
      </button>
      {open && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          marginTop: 6,
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: 10,
          boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
          zIndex: 100,
          minWidth: 240,
          maxHeight: 260,
          overflowY: 'auto',
          padding: 6,
        }}>
          {options.map((opt) => (
            <div
              key={opt}
              onClick={async () => {
                setSaving(true);
                try { await onSave(opt); setOpen(false); }
                finally { setSaving(false); }
              }}
              style={{
                padding: '0.5rem 0.65rem',
                borderRadius: 8,
                cursor: 'pointer',
                background: opt === value ? '#f3f4f6' : 'transparent',
                fontWeight: opt === value ? 700 : 500,
                color: '#111827',
                fontSize: '0.85rem',
              }}
              onMouseEnter={(e) => { if (opt !== value) e.currentTarget.style.background = '#f9fafb'; }}
              onMouseLeave={(e) => { if (opt !== value) e.currentTarget.style.background = 'transparent'; }}
            >
              {opt}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PrimaryTimeSlotDropdown({ value, studentTimezone, onSave }) {
  const [open, setOpen] = useState(false);
  const [day, setDay] = useState(DAYS[0]);
  const [hour, setHour] = useState(9);
  const [saving, setSaving] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const display = value ? displayPrimaryTimeSlot(value, studentTimezone) : '—';
  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        disabled={saving}
        style={{
          background: 'transparent',
          border: 'none',
          padding: 0,
          cursor: saving ? 'wait' : 'pointer',
          fontSize: '0.82rem',
          fontWeight: 600,
          color: value ? '#166534' : '#bbb',
          textDecoration: 'underline',
          textUnderlineOffset: 3,
        }}
        title="Override first class"
      >
        {display}
      </button>
      {open && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          marginTop: 8,
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: 12,
          boxShadow: '0 8px 22px rgba(0,0,0,0.14)',
          zIndex: 120,
          padding: 10,
          minWidth: 260,
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <select value={day} onChange={(e) => setDay(e.target.value)} style={pickerSelect}>
              {DAYS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
            <select value={hour} onChange={(e) => setHour(Number(e.target.value))} style={pickerSelect}>
              {HOURS.map((h) => {
                const period = h >= 12 ? 'pm' : 'am';
                let h12 = h % 12; if (h12 === 0) h12 = 12;
                return <option key={h} value={h}>{h12}{period}</option>;
              })}
            </select>
          </div>
          <div style={{ marginTop: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
              Preview: <span style={{ fontWeight: 700, color: '#111827' }}>{formatFirstClassFromPicker(day, hour)}</span>
            </div>
            <button
              type="button"
              onClick={async () => {
                setSaving(true);
                try { await onSave(formatFirstClassFromPicker(day, hour)); setOpen(false); }
                finally { setSaving(false); }
              }}
              style={{ ...btnPrimarySmall, opacity: saving ? 0.7 : 1 }}
              disabled={saving}
            >
              {saving ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <span className="loading-spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />
                  Saving...
                </span>
              ) : (
                'Save'
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Th({ children, style, onClick, active, dir }) {
  const clickable = typeof onClick === 'function';
  return (
    <th
      style={{
        padding: '0.75rem 1rem',
        fontSize: '0.78rem',
        fontWeight: 600,
        color: '#777',
        textTransform: 'uppercase',
        letterSpacing: '0.03em',
        textAlign: 'left',
        cursor: clickable ? 'pointer' : 'default',
        userSelect: 'none',
        ...style,
      }}
      onClick={onClick}
    >
      <span>{children}</span>
      {active && (
        <span style={{ marginLeft: 6, color: '#166534', fontWeight: 900 }}>
          {dir === 'asc' ? '▲' : '▼'}
        </span>
      )}
    </th>
  );
}
function Td({ children, style }) {
  return <td style={{ padding: '0.75rem 1rem', fontSize: '0.88rem', ...style }}>{children}</td>;
}

function MatchStatusBadge({ status }) {
  const configs = {
    'Booked': { bg: '#14532d', color: '#fff', label: 'Booked' },
    'Matched': { bg: '#16a34a', color: '#fff', label: 'Matched' },
    'Resolved': { bg: '#0d9488', color: '#fff', label: 'Resolved' },
    'Awaiting Selection': { bg: '#f59e0b', color: '#fff', label: 'Awaiting' },
    'Needs Attention': { bg: '#ef4444', color: '#fff', label: 'Needs Attention' },
    // Fallback if any rows still have raw "Match Found"
    'Match Found': { bg: '#f59e0b', color: '#fff', label: 'Awaiting' },
    'Class changed': { bg: '#8b5cf6', color: '#fff', label: 'Class changed' },
    'Pending': { bg: '#f59e0b', color: '#fff', label: 'Pending' },
    'Matching in progress': { bg: '#3b82f6', color: '#fff', label: 'Processing' },
    'Blocked': { bg: '#ef4444', color: '#fff', label: 'Blocked' },
  };
  const cfg = configs[status] || configs['Pending'];
  return <span style={{ display: 'inline-block', padding: '0.25rem 0.75rem', borderRadius: 4, background: cfg.bg, color: cfg.color, fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.02em' }}>{cfg.label}</span>;
}

/** Resolve destinations only (row is always Blocked / Needs Attention when this panel shows). */
const NEEDS_ATTENTION_RESOLVE_OPTIONS = [
  // Needs Attention should only allow a direct move to Matched.
  { value: 'Matched', label: 'Matched' },
];

function NeedsAttentionResolvePanel({ request, onUpdated }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const pick = async (nextStatus) => {
    if (saving) return;
    setOpen(false);
    setSaving(true);
    try {
      const { data } = await axios.patch(`${API_BASE}/api/dashboard/${request.id}/matching-status`, {
        status: nextStatus,
        clear_blocked_state: true,
        // Ensures "Resolved" (Match Found) stores the payload flag even if the server mis-read Blocked
        resolve_from_needs_attention: nextStatus === 'Match Found',
        // Backend sends parent confirmation (student form path never ran for these rows)
        notify_parent: nextStatus === 'Matched',
      });
      if (data?.email && data.email.sent === false && data.email.reason) {
        alert(`Status updated. Parent email was not sent: ${data.email.reason}\n\nCheck backend .env: EMAIL_ENABLED=true and SMTP_* / SENDER_* vars.`);
      }
      await onUpdated();
    } catch (err) {
      alert(err.response?.data?.error || err.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      ref={ref}
      style={{
        textAlign: 'left',
        maxWidth: 240,
        margin: '0 auto',
        borderRadius: 10,
        border: '1px solid #fecaca',
        background: 'linear-gradient(180deg, #fff7f7 0%, #fff 100%)',
        boxShadow: '0 1px 3px rgba(185, 28, 28, 0.08)',
        overflow: 'hidden',
      }}
    >
      <button
        type="button"
        disabled={saving}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
          padding: '0.65rem 0.75rem',
          border: 'none',
          background: '#fff1f2',
          cursor: saving ? 'wait' : 'pointer',
        }}
        aria-label="Needs attention resolve dropdown"
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <AlertCircle size={16} strokeWidth={2.25} color="#b91c1c" aria-hidden />
          <span style={{ background: '#ef4444', color: '#fff', padding: '0.45rem 0.7rem', borderRadius: 8, fontWeight: 800, fontSize: '0.9rem' }}>
            Needs Attention
          </span>
        </div>
          {saving ? (
            <span className="loading-spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />
          ) : (
            <ChevronDown
              size={16}
              style={{
                opacity: 0.9,
                transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 120ms ease',
              }}
              aria-hidden
            />
          )}
      </button>

      {open && (
        <div
          style={{
            marginTop: 8,
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: 10,
            boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
            overflow: 'hidden',
            zIndex: 120,
          }}
        >
            {NEEDS_ATTENTION_RESOLVE_OPTIONS.map((o) => (
              <div
                key={o.value}
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  pick(o.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') pick(o.value);
                }}
                style={{
                  padding: '0.55rem 0.7rem',
                  cursor: 'pointer',
                  fontWeight: 700,
                  fontSize: '0.85rem',
                  color: '#111827',
                  background: 'transparent',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#f9fafb'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                {o.label}
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

function AwaitingResolvePanel({ request, onUpdated }) {
  const [saving, setSaving] = useState(false);

  const markResolved = async (e) => {
    e.stopPropagation();
    if (saving) return;
    setSaving(true);
    try {
      const { data } = await axios.patch(`${API_BASE}/api/dashboard/${request.id}/matching-status`, {
        status: 'Match Found',
        // Reuse the existing server flag so Match Found renders as "Resolved" (not Awaiting).
        resolve_from_needs_attention: true,
        clear_blocked_state: false,
        // Client requirement: resolving Awaiting rows should not send parent email.
        notify_parent: false,
      });
      if (data?.email && data.email.sent === false && data.email.reason) {
        alert(`Resolved. Parent email was not sent: ${data.email.reason}\n\nCheck backend .env: EMAIL_ENABLED=true and SMTP_* / SENDER_* vars.`);
      }
      await onUpdated?.();
    } catch (err) {
      alert(err.response?.data?.error || err.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
      <MatchStatusBadge status={request.matching_status} />
      <button
        type="button"
        onClick={markResolved}
        disabled={saving}
        style={{
          border: '1px solid #99f6e4',
          background: '#f0fdfa',
          color: '#0f766e',
          borderRadius: 999,
          padding: '0.2rem 0.6rem',
          fontSize: '0.74rem',
          fontWeight: 700,
          cursor: saving ? 'wait' : 'pointer',
          lineHeight: 1.35,
        }}
        title="Mark this manual coach selection as Resolved"
      >
        {saving ? 'Saving...' : 'Resolve'}
      </button>
    </div>
  );
}

function BookedCheckboxCell({ request, onUpdated }) {
  const [saving, setSaving] = useState(false);
  const isBooked = String(request?.matching_status || '') === 'Booked';

  const markBooked = async (e) => {
    e.stopPropagation();
    if (saving || isBooked) return;
    setSaving(true);
    try {
      await axios.patch(`${API_BASE}/api/dashboard/${request.id}/matching-status`, {
        status: 'Booked',
        clear_blocked_state: false,
      });
      await onUpdated?.();
    } catch (err) {
      alert(err.response?.data?.error || err.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
      <button
        type="button"
        onClick={markBooked}
        disabled={saving || isBooked}
        aria-label={isBooked ? 'Booked complete' : 'Mark as booked'}
        title={isBooked ? 'Booked complete' : 'Click to mark booked'}
        style={{
          width: 22,
          height: 22,
          borderRadius: 6,
          border: isBooked ? '1px solid #166534' : '1px solid #9ca3af',
          background: isBooked ? '#166534' : '#fff',
          color: '#fff',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: (saving || isBooked) ? 'default' : 'pointer',
          opacity: saving ? 0.7 : 1,
          padding: 0,
        }}
      >
        {saving ? (
          <span className="loading-spinner" style={{ width: 12, height: 12, borderWidth: 2 }} />
        ) : isBooked ? (
          <Check size={14} strokeWidth={3} />
        ) : null}
      </button>
    </div>
  );
}

function dayToIndex(day) {
  const d = String(day || '').trim().toLowerCase();
  if (d.startsWith('mon')) return 1;
  if (d.startsWith('tue')) return 2;
  if (d.startsWith('wed')) return 3;
  if (d.startsWith('thu')) return 4;
  if (d.startsWith('fri')) return 5;
  if (d.startsWith('sat')) return 6;
  if (d.startsWith('sun')) return 0;
  return null;
}

function parseHourFromHm(hm) {
  const m = String(hm || '').trim().match(/^(\d{1,2})(?::(\d{2}))?$/);
  if (!m) return null;
  const h = Number(m[1]);
  return Number.isFinite(h) ? h : null;
}

function parseSelectedSlotDayHour(slotStr) {
  const s = String(slotStr || '');
  const dayMatch = s.match(/^(Mon|Tue|Wed|Thu|Fri|Sat|Sun)/i);
  const timeMatch = s.match(/at\s+(\d{1,2}):(\d{2})\s*(AM|PM)/i);
  if (!dayMatch || !timeMatch) return { dayIndex: null, hour24: null };
  const dayIndex = dayToIndex(dayMatch[1]);
  let hour = Number(timeMatch[1]);
  const isPm = String(timeMatch[3]).toUpperCase() === 'PM';
  if (hour === 12) hour = 0;
  if (isPm) hour += 12;
  return { dayIndex, hour24: hour };
}

function getSlotAvailabilityTone(slotStr, availabilitySlots) {
  const slots = Array.isArray(availabilitySlots) ? availabilitySlots : [];
  if (!slotStr || slots.length === 0) return 'warn';
  const { dayIndex, hour24 } = parseSelectedSlotDayHour(slotStr);
  if (dayIndex == null || hour24 == null) return 'warn';

  const rangesSameDay = slots
    .filter((s) => dayToIndex(s?.day) === dayIndex)
    .map((s) => ({ start: parseHourFromHm(s?.start), end: parseHourFromHm(s?.end) }))
    .filter((r) => r.start != null && r.end != null);

  if (rangesSameDay.some((r) => hour24 >= r.start && hour24 < r.end)) return 'match';
  if (rangesSameDay.length > 0) return 'warn';
  return 'mismatch';
}

function ExpandedRow({ request }) {
  const r = request;
  const sorted = [...(r.top_matches || [])].sort((a, b) => (b.score_pct || 0) - (a.score_pct || 0));
  const matchSlotCounts = sorted.map((m) => ({
    coach_name: m?.coach_name || '',
    count: ((m?.exact_slots || []).length + (m?.nearby_slots || []).length)
  }));
  const countsOnly = matchSlotCounts.map(x => x.count);
  const maxSlots = countsOnly.length ? Math.max(...countsOnly) : 0;
  const positiveCounts = countsOnly.filter((n) => Number(n) > 0);
  const minSlots = positiveCounts.length ? Math.min(...positiveCounts) : 0;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
      <div><div style={detailLabel}>Student Availability</div><div style={detailValue}>{r.availability_summary || 'Not specified'}</div></div>
      <div><div style={detailLabel}>Timezone</div><div style={detailValue}>{r.timezone || '—'}</div></div>
      {r.preferred_coach && (
        <div>
          <div style={detailLabel}>Preferred Coach (from comments)</div>
          <div style={detailValue}>{r.preferred_coach}</div>
        </div>
      )}
      {r.selected_coach && (
        <div>
          <div style={detailLabel}>Selected Coach</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <span style={detailValue}>{r.selected_coach}</span>
            {r.calendly_url && <a href={r.calendly_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.85rem', color: '#6366f1', fontWeight: 600, textDecoration: 'none' }} onClick={e => e.stopPropagation()}>📅 Calendly Scheduling Link</a>}
          </div>
        </div>
      )}
      {((r.selected_slots?.length > 0) || r.selected_slot) && (
        <div style={{ gridColumn: '1 / -1' }}>
          {r.primary_time_slot && (
            <div style={{ marginBottom: '0.5rem' }}>
              <div style={detailLabel}>First class</div>
              <div style={{ ...detailValue, fontWeight: 700, color: '#166534' }}>{displayPrimaryTimeSlot(r.primary_time_slot, r.timezone)}</div>
            </div>
          )}
          <div style={detailLabel}>Selected Time Slots</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.25rem' }}>
            {(r.selected_slots?.length > 0 ? r.selected_slots : [r.selected_slot]).filter(Boolean).map((slot, i) => {
              const isOutsidePrimary = r.primary_time_slot && !slotMatchesPrimaryEastern(String(slot), r.primary_time_slot, r.timezone);
              const tone = getSlotAvailabilityTone(String(slot), r.availability_slots);
              const bg = tone === 'match' ? '#dcfce7' : tone === 'mismatch' ? '#fee2e2' : '#fef9c3';
              const color = tone === 'match' ? '#166534' : tone === 'mismatch' ? '#991b1b' : '#854d0e';
              const border = tone === 'match' ? '#bbf7d0' : tone === 'mismatch' ? '#fecaca' : '#fde68a';
              return (
                <span
                  key={i}
                  style={{
                    display: 'inline-block',
                    background: isOutsidePrimary && tone === 'match' ? '#fef9c3' : bg,
                    color: isOutsidePrimary && tone === 'match' ? '#854d0e' : color,
                    border: `1px solid ${isOutsidePrimary && tone === 'match' ? '#fde68a' : border}`,
                    padding: '0.3rem 0.75rem',
                    borderRadius: 6,
                    fontSize: '0.8rem',
                    fontWeight: 600,
                  }}
                >
                  {formatSlotLineForDashboard(String(slot), r.timezone)}
                </span>
              );
            })}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.45rem' }}>
            <span style={{ fontSize: '0.72rem', color: '#166534', background: '#dcfce7', border: '1px solid #bbf7d0', borderRadius: 999, padding: '2px 8px', fontWeight: 600 }}>
              Green = Matches availability
            </span>
            <span style={{ fontSize: '0.72rem', color: '#854d0e', background: '#fef9c3', border: '1px solid #fde68a', borderRadius: 999, padding: '2px 8px', fontWeight: 600 }}>
              Yellow = Adjustment needed
            </span>
            <span style={{ fontSize: '0.72rem', color: '#991b1b', background: '#fee2e2', border: '1px solid #fecaca', borderRadius: 999, padding: '2px 8px', fontWeight: 600 }}>
              Red = Mismatch
            </span>
          </div>
        </div>
      )}
      <div><div style={detailLabel}>Term Start Date</div><div style={detailValue}>{r.term_start_date || '—'}</div></div>
      {Array.isArray(r.term_start_dates) && r.term_start_dates.length > 1 && (
        <div>
          <div style={detailLabel}>All Term Start Dates</div>
          <div style={detailValue}>{r.term_start_dates.join(', ')}</div>
        </div>
      )}
      <div style={{ gridColumn: '2 / -1' }}><div style={detailLabel}>Comments</div><div style={detailValue}>{r.comments || 'None'}</div></div>
      {r.declined_options && (r.declined_options.reason || r.declined_options.contact) && (
        <div style={{ gridColumn: '1 / -1' }}>
          <div style={detailLabel}>Parent — none of these coaches work</div>
          <div style={{
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: 8,
            padding: '0.85rem 1rem',
            fontSize: '0.88rem',
            lineHeight: 1.55,
            color: '#374151',
          }}>
            <div style={{ marginBottom: '0.5rem' }}>
              <span style={{ fontWeight: 700, color: '#991b1b' }}>Feedback: </span>
              {r.declined_options.reason || '—'}
            </div>
            <div style={{ marginBottom: r.declined_options.submitted_at ? '0.5rem' : 0 }}>
              <span style={{ fontWeight: 700, color: '#991b1b' }}>Contact: </span>
              {r.declined_options.contact || '—'}
            </div>
            {r.declined_options.submitted_at && (
              <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                Submitted {formatDashboardTimestamp(r.declined_options.submitted_at)}
              </div>
            )}
          </div>
        </div>
      )}
      {sorted.length > 0 && (
        <div style={{ gridColumn: '1 / -1' }}>
          <div style={detailLabel}>Top Matching Coaches</div>
          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.4rem', flexWrap: 'wrap' }}>
            {sorted.map((m, i) => (
              <CoachMatchCard
                key={i}
                match={m}
                request={r}
                rank={i + 1}
                maxSlots={maxSlots}
                minSlots={minSlots}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function CoachMatchCard({ match, request, rank, maxSlots, minSlots }) {
  const navigate = useNavigate();
  const m = match;
  const r = request;
  const compatibleSlotsCount = (m?.exact_slots || []).length + (m?.nearby_slots || []).length;
  const showMaxTag = compatibleSlotsCount > 0 && compatibleSlotsCount === maxSlots && maxSlots > 0;
  const showMinTag = compatibleSlotsCount > 0 && compatibleSlotsCount === minSlots && (maxSlots !== minSlots);

  const goToCoach = (e) => {
    e.stopPropagation();
    const name = String(m.coach_name || '').trim();
    if (!name) return;
    navigate(`/coaches?coach=${encodeURIComponent(name)}`);
  };

  return (
    <div
      onClick={goToCoach}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') goToCoach(e); }}
      style={{
        background: '#fff',
        border: '1px solid #e5e5e5',
        borderRadius: 8,
        padding: '0.75rem 1rem',
        minWidth: 200,
        flex: 1,
        cursor: 'pointer'
      }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#c1009d'; e.currentTarget.style.boxShadow = '0 4px 14px rgba(0,0,0,0.06)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e5e5e5'; e.currentTarget.style.boxShadow = 'none'; }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontWeight: 600, color: '#222', fontSize: '0.9rem' }}>#{rank} {m.coach_name}</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {m.calendly_scheduling_url && <a href={m.calendly_scheduling_url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={{ fontSize: '0.7rem', color: '#6366f1', fontWeight: 600, textDecoration: 'none' }}>📅 Book</a>}
          <span style={{ background: scoreColor(m.score_pct), color: '#fff', borderRadius: 12, padding: '0.15rem 0.55rem', fontSize: '0.72rem', fontWeight: 700 }}>{m.score_pct}%</span>
        </span>
      </div>
      <div style={{ marginTop: '0.4rem', display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
        <span style={{ background: '#eef2ff', color: '#3730a3', borderRadius: 999, padding: '0.15rem 0.55rem', fontSize: '0.72rem', fontWeight: 700 }}>
          {compatibleSlotsCount} slots available
        </span>
      </div>
      {r.selected_coach && (
        <div style={{ marginTop: '0.4rem', display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
          {String(r.selected_coach).trim() === String(m.coach_name || '').trim() ? (
            <span style={{ background: '#dcfce7', color: '#166534', borderRadius: 999, padding: '0.15rem 0.55rem', fontSize: '0.72rem', fontWeight: 700 }}>Selected</span>
          ) : (
            <span style={{ background: '#f3f4f6', color: '#6b7280', borderRadius: 999, padding: '0.15rem 0.55rem', fontSize: '0.72rem', fontWeight: 700 }}>Not selected</span>
          )}
        </div>
      )}
      {m.reasoning && <div style={{ fontSize: '0.78rem', color: '#666', marginTop: '0.35rem', lineHeight: 1.4 }}>{m.reasoning.length > 120 ? m.reasoning.substring(0, 120) + '...' : m.reasoning}</div>}
      <div style={{ marginTop: '0.5rem', fontSize: '0.76rem', color: '#555', lineHeight: 1.35 }}>
        <div style={{ fontWeight: 700, color: '#111827' }}>Schedule compatibility</div>
        <div>
          Exact slots: <span style={{ fontWeight: 700 }}>{(m.exact_slots || []).length}</span> · Nearby slots: <span style={{ fontWeight: 700 }}>{(m.nearby_slots || []).length}</span>
        </div>
        {showMaxTag && (
          <div style={{ marginTop: '0.25rem', color: '#166534', fontWeight: 600, fontStyle: 'italic' }}>
            Maximum slots available
          </div>
        )}
        {showMinTag && (
          <div style={{ marginTop: '0.25rem', color: '#92400e', fontWeight: 600, fontStyle: 'italic' }}>
            Minimum slots available
          </div>
        )}
        {(m.exact_slots || []).length === 0 && (m.nearby_slots || []).length === 0 && (
          <div style={{ marginTop: '0.25rem', color: '#b45309', fontWeight: 600, fontStyle: 'italic' }}>
            No compatible dates/times found for the student's availability in the bootcamp range.
          </div>
        )}
        {m.calendly_error && (
          <div style={{ marginTop: '0.25rem', color: '#991b1b', fontWeight: 600 }}>
            Calendly check issue: {String(m.calendly_error)}
          </div>
        )}
      </div>
    </div>
  );
}

function scoreColor(score) {
  const s = Number(score) || 0;
  if (s >= 90) return '#14532d'; // dark green (green-900)
  if (s >= 75) return '#166534'; // medium green (green-800)
  if (s >= 60) return '#3f6212'; // light green (lime-800)
  if (s >= 45) return '#c2410c'; // orange (orange-700)
  if (s >= 30) return '#a16207'; // yellow (yellow-700)
  if (s >= 15) return '#be123c'; // pink-red (rose-700)
  return '#6b7280'; // gray
}

const detailLabel = { fontSize: '0.72rem', fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.2rem' };
const detailValue = { fontSize: '0.85rem', color: '#333', lineHeight: 1.5 };
const btnSecondary = { display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.5rem 1rem', borderRadius: 8, border: '1px solid #ddd', background: '#fff', color: '#555', fontSize: '0.82rem', cursor: 'pointer', fontWeight: 500 };
const miniIconBtn = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #e5e7eb', background: '#fff', borderRadius: 8, padding: 6, cursor: 'pointer' };
const pickerSelect = { width: '100%', padding: '0.45rem 0.6rem', border: '1px solid #e5e7eb', borderRadius: 10, fontSize: '0.85rem', background: '#fff' };
const btnPrimarySmall = { background: BRAND, color: '#fff', border: 'none', borderRadius: 10, padding: '0.45rem 0.75rem', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer' };

export default Dashboard;
